import React, { useState, useEffect, useCallback } from 'react';
import QuestionLearnButton from '../full_mock_test/attempt/QuestionLearnButton';
import '../../styles/ExerciseLayout.css';

const ReadingPart5 = ({ exerciseData, attemptId, exerciseId, showHint, isReviewMode, fillHintSignal }) => {
  const [answers, setAnswers] = useState({});
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    if (attemptId && exerciseId) {
      // 1. Prioritize API data in review mode
      if (isReviewMode && exerciseData.questions) {
        const migratedAnswers = {};
        exerciseData.questions.forEach(q => {
          if (q.user_answer && typeof q.user_answer.answer === 'string') {
            migratedAnswers[q.id] = q.user_answer.answer;
          }
        });
        setAnswers(migratedAnswers);
        setIsHydrated(true);
        return;
      }

      // 2. Otherwise load from localStorage
      const attemptKey = `attempt_${attemptId}`;
      const savedAttempt = localStorage.getItem(attemptKey);
      if (savedAttempt) {
        try {
          const allAnswers = JSON.parse(savedAttempt);
          const savedData = allAnswers[exerciseId];
          if (savedData) {
            const migratedAnswers = {};
            Object.keys(savedData).forEach(qId => {
              const value = savedData[qId];
              migratedAnswers[qId] = (typeof value === 'object' && value !== null && 'answer' in value) ? value.answer : value;
            });
            setAnswers(migratedAnswers);
          }
        } catch (e) {
          console.error("Failed to parse saved attempt from localStorage", e);
        }
      }
      setIsHydrated(true);
    }
  }, [attemptId, exerciseId, isReviewMode, exerciseData.questions]);

  const saveAnswersToLocalStorage = useCallback((currentAnswers) => {
    if (!isHydrated || !attemptId || !exerciseId) return;

    const attemptKey = `attempt_${attemptId}`;
    let allAnswers = {};
    try {
      const savedAttempt = localStorage.getItem(attemptKey);
      if (savedAttempt) allAnswers = JSON.parse(savedAttempt) || {};
    } catch (e) {
      allAnswers = {};
    }

    const answersToSave = {};
    Object.keys(currentAnswers).forEach(qId => {
      answersToSave[qId] = { answer: currentAnswers[qId] };
    });

    allAnswers[exerciseId] = answersToSave;
    localStorage.setItem(attemptKey, JSON.stringify(allAnswers));
  }, [attemptId, exerciseId, isHydrated]);

  useEffect(() => {
    if (fillHintSignal > 0) {
      const newAnswers = {};
      exerciseData.questions.forEach(q => {
        if (q.accepted_answers && q.accepted_answers.length > 0) {
          newAnswers[q.id] = q.accepted_answers[0];
        }
      });
      setAnswers(newAnswers);
      saveAnswersToLocalStorage(newAnswers);
    }
  }, [fillHintSignal, exerciseData.questions, saveAnswersToLocalStorage]);

  useEffect(() => {
    if (!isHydrated) return;
    const debounceSave = setTimeout(() => {
      saveAnswersToLocalStorage(answers);
    }, 500);
    return () => {
      clearTimeout(debounceSave);
      saveAnswersToLocalStorage(answers);
    };
  }, [answers, isHydrated, saveAnswersToLocalStorage]);

  if (!exerciseData || !exerciseData.content_data || !exerciseData.questions) {
    return <div>Loading exercise data...</div>;
  }

  const {
    image_url,
    reading_text,
    story_title,
  } = exerciseData.content_data;

  const exampleQuestions = exerciseData.questions.filter(q => q.order_index === 0);
  const mainQuestions = exerciseData.questions.filter(q => q.order_index > 0).sort((a, b) => a.order_index - b.order_index);

  const ASSET_BASE_URL = process.env.REACT_APP_MEDIA_PREFIX || 'http://localhost:8000';

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const renderSentence = (question, isExample) => {
    const parts = question.question_text.split(/(\{\{gap\}\}|\{\{big_gap\}\})/g);
    return parts.map((part, index) => {
      if (part === '{{gap}}' || part === '{{big_gap}}') {
        if (isExample) {
          return <strong key={index} className="exercise-example-text px-1">{question.accepted_answers[0]}</strong>;
        }
        return (
          <span key={question.id}>
            {(() => {
              // Fix: is_correct is at top level
              const isAnswerCorrect = isReviewMode && (question.is_correct === true || question.is_correct === 'true');
              const showIncorrectAnswerInReview = isReviewMode && !isAnswerCorrect;

              return (
                <>
                  {showIncorrectAnswerInReview ? (
                    <span className="exercise-input-text inline-flex items-center bg-red-50 dark:bg-red-900/20 px-2 py-0.5 rounded-lg border border-red-100 dark:border-red-800 mx-1 align-middle">
                      {question.user_answer?.answer
                        ? <span style={{ textDecoration: 'line-through' }} className="text-red-500 opacity-70 mr-2 font-bold">{question.user_answer.answer}</span>
                        : <span className="text-gray-400 mr-2 font-bold">__</span>
                      }
                      <span className="text-green-600 font-bold">{question.accepted_answers[0]}</span>
                    </span>
                  ) : (
                    <input
                      type="text"
                      autoComplete="off"
                      value={(showHint || isAnswerCorrect) ? question.accepted_answers[0] : (answers[question.id] || '')}
                      onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                      className={`exercise-input-field exercise-input-text text-center align-middle text-sm px-1 py-1 ${showHint ? 'hint-input' : ''}`}
                      style={{ width: Math.max(100, (question.accepted_answers?.[0]?.length || 6) * 12 + 24) }}
                      readOnly={showHint || isReviewMode || !isHydrated}
                      disabled={isReviewMode || !isHydrated}
                    />
                  )}
                  {isReviewMode && (isAnswerCorrect
                    ? <span className="correct-checkmark scale-75">✓</span>
                    : <span className="incorrect-checkmark scale-75">✗</span>
                  )}
                  {isReviewMode && (
                    <span className="ml-1 align-middle inline-flex"><QuestionLearnButton question={question} size={18} /></span>
                  )}
                </>
              )
            })()}
          </span>
        );
      }
      return <span key={index}>{part}</span>;
    });
  };

  return (
    <div className="w-full h-full flex flex-col p-4 md:px-10">
      <div className="flex-1 flex flex-col min-h-0">
        <div className="space-y-6">
          <div className="exercise-story-box !mt-0 p-6 md:p-8 shadow-sm border border-slate-100 rounded-3xl bg-white dark:bg-slate-800/40">
            {story_title && (
              <div className="text-center w-full">
                <h3 className="exercise-story-title mb-6 text-2xl md:text-3xl font-display font-black text-slate-800 dark:text-white leading-tight">
                  {story_title.replace(/\(Example\)/gi, '').trim()}
                </h3>
              </div>
            )}
            <div className="space-y-4 exercise-body-text text-base md:text-lg leading-relaxed text-slate-700 dark:text-slate-300 text-left">
              {reading_text.split('\n').map((paragraph, i) => <p key={i} className="m-0 leading-snug">{paragraph}</p>)}
            </div>
          </div>

          <div className="exercise-card p-6 md:p-8 shadow-sm border border-slate-100 rounded-3xl bg-white dark:bg-slate-800/40">
            <div className="space-y-6">
              <div className="space-y-3">
                <h4 className="font-display font-black text-xs uppercase tracking-widest text-slate-300 mb-4">Examples</h4>
                {exampleQuestions.map(q => (
                  <div key={q.id} className="exercise-body-text text-base md:text-lg font-bold text-slate-500 italic bg-slate-50 dark:bg-slate-900/50 p-3 rounded-xl border border-transparent">
                    <span className="shrink-0 font-black text-white bg-amber-500 px-2 py-0.5 rounded-lg text-[10px] uppercase tracking-wider shadow-sm mr-3">e.g.</span>
                    {renderSentence(q, true)}
                  </div>
                ))}
              </div>

              <div className="space-y-4 pt-6 border-t border-slate-100 dark:border-slate-800">
                <h4 className="font-display font-black text-xs uppercase tracking-widest text-slate-300 mb-4">Questions</h4>
                {mainQuestions.map((q, index) => (
                  <div key={q.id} className="exercise-body-text text-base md:text-lg flex items-start gap-4 p-2">
                    <span className="font-black text-slate-400 bg-slate-50 dark:bg-slate-900 px-3 py-1.5 rounded-xl text-sm border border-slate-100 dark:border-slate-800 shadow-sm shrink-0">
                      {index + 1}.
                    </span>
                    <span className="font-bold text-slate-800 dark:text-white leading-relaxed">{renderSentence(q, false)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReadingPart5;
